/**
 * Detects and bridges theme detection for model-driven apps (Power Apps)
 * When embedded in an iframe, this utility helps detect the parent app's dark mode
 */

import { isInModelDrivenAppContext } from '../components/Contexts/UIContext/uiCtx';

export type ThemeDetectionSource = 'system' | 'parent-computed' | 'parent-message';

type ParentTheme = {
    isDark: boolean;
    source: ThemeDetectionSource;
};

/**
 * Detects dark mode from parent frame's computed styles
 * Looks for Fluent UI theme tokens in parent's CSS custom properties
 */
export const detectParentDarkMode = (): ParentTheme | null => {
    if (!isInModelDrivenAppContext) {
        return null;
    }

    try {
        // Access parent window's computed styles
        const parentRoot = globalThis.parent?.document?.documentElement;
        if (!parentRoot) {
            return null;
        }

        const parentStyles = globalThis.parent!.getComputedStyle(parentRoot);

        // Check for Fluent tokens that indicate dark theme
        // In dark mode, colorNeutralBackground1 is dark (#ffffff in light, #1f1f1f in dark)
        const bgColor = parentStyles.getPropertyValue('--colorNeutralBackground1')?.trim();
        const neutralForeground = parentStyles.getPropertyValue('--colorNeutralForeground1')?.trim();

        // If background is dark and foreground is light, it's dark mode
        if (bgColor && neutralForeground) {
            // Rough check: if background is dark (low brightness), it's dark mode
            const isDark = isBgColorDark(bgColor);
            return {
                isDark,
                source: 'parent-computed',
            };
        }

        // Fallback: check parent body class for dark mode indicator
        if (globalThis.parent?.document?.body?.classList?.contains('isDarkmode')) {
            return {
                isDark: true,
                source: 'parent-computed',
            };
        }

        // Another fallback: check for data attributes
        const dataTheme = globalThis.parent?.document?.documentElement?.dataset.theme;
        if (dataTheme) {
            return {
                isDark: dataTheme === 'dark',
                source: 'parent-computed',
            };
        }

        return null;
    } catch (error) {
        if (error instanceof DOMException && error.name === 'SecurityError') {
            // Cross-origin iframe restrictions are expected in some embeds.
            return null;
        }

        throw error;
    }
};

/**
 * Simple heuristic to detect if a CSS color string represents a dark background
 */
function isBgColorDark(colorStr: string): boolean {
    // Remove spaces
    const color = colorStr.trim().toLowerCase();

    // Parse hex color
    if (color.startsWith('#')) {
        const hex = color.slice(1);
        if (hex.length === 6) {
            const r = Number.parseInt(hex.substring(0, 2), 16);
            const g = Number.parseInt(hex.substring(2, 4), 16);
            const b = Number.parseInt(hex.substring(4, 6), 16);
            // Calculate luminance
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            return luminance < 0.5; // Dark if luminance < 50%
        }
    }

    // Parse rgb/rgba
    const rgbRegex = /rgba?\((\d+),\s*(\d+),\s*(\d+)/;
    const rgbMatch = rgbRegex.exec(color);
    if (rgbMatch) {
        const r = Number.parseInt(rgbMatch[1], 10);
        const g = Number.parseInt(rgbMatch[2], 10);
        const b = Number.parseInt(rgbMatch[3], 10);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance < 0.5;
    }

    // Default to light mode if we can't determine
    return false;
}

/**
 * Get combined dark mode state with fallback chain:
 * 1. Parent frame detection (if in model-driven app)
 * 2. System preference (fallback)
 */
export const getCombinedDarkModeState = (systemDarkMode: boolean): boolean => {
    const parentTheme = detectParentDarkMode();
    if (parentTheme) {
        return parentTheme.isDark;
    }
    return systemDarkMode;
};
